import * as localforage from 'localforage';

import { CopyAction, DescriptorAction, OpenAction, PathActions, ReadAction, WriteAction } from './Wrapper';
import { getEnvVar } from '../../utils/ReactScriptHelper';
import { _sendReply } from '../Communication';

interface FileWrapper {
    read(size: number): Promise<string>;
    write(data: string): Promise<void>;
}

const db: LocalForage = ((): LocalForage => {
    return localforage.createInstance({
        name: getEnvVar('ID', 'missing-env-id'),
        version: 1.0,
        storeName: 'fsapi',
        description: 'Debug implementation of the app file system api'
    });
})();
const fdIndex: {[id: string]: FileWrapper} = {};
let fdCount: number = 0;

function storageKeys(): Promise<Array<string>> {
    return db.keys();
}

async function storageGet(key: string): Promise<string> {
    let value: string = await db.getItem(key) as string;

    if (value != null) {
        value = atob(value);
    }

    return value;
}

async function storageSet(key: string, value: string): Promise<void> {
    await db.setItem(key, btoa(value));
}

async function storageDel(key: string): Promise<void> {
    return db.removeItem(key);
}

async function storageAdd(key: string, value: string): Promise<void> {
    const content: string = await storageGet(key);
    return storageSet(
        key,
        content + value
    );
}

function unifyPath(path: string, onlyFolderAllowed: boolean = false): string {
    const regexFolder: RegExp = /^([a-z0-9\.\-_]+)$/m;
    const regexFilename: RegExp = /^([a-z0-9\.\-_]+)\.([a-z0-9]+)$/m;

    if (path[0] !== '/') {
        throw new Error('Unsupported path');
    }

    if (onlyFolderAllowed === true && !path.endsWith('/')) {
        throw new Error('Only folders allowed');
    }

    const parts: Array<string> = path.split('/').slice(1); // path starts with '/' -> first index is always empty

    const folders: Array<string> = parts.slice(0, -1);
    let filename: string | null = null;
    if (!path.endsWith('/')) {
        filename = parts[parts.length - 1];
    }

    for (const folder of folders) {
        if (folder[0] === '.' || !regexFolder.test(folder)) {
            throw new Error('Unsupported path');
        }
    }

    if (filename != null && (filename[0] === '.' || !regexFilename.test(filename))) {
        throw new Error('Unsupported filename');
    }

    // tslint:disable-next-line
    return path.toLowerCase();
}

function getFileName(path: string): string {
    path = unifyPath(path);

    return path.slice(path.lastIndexOf('/'), path.length);
}

interface Operation<ActionType, ArgumentsType> {
    transaction_id: number;

    action: ActionType;
    arguments: ArgumentsType;
}

type Operations = Operation<'close', DescriptorAction> |
                  Operation<'open', OpenAction> |
                  Operation<'flush', DescriptorAction> |
                  Operation<'write', WriteAction> |
                  Operation<'read', ReadAction> |
                  Operation<'remove', PathActions> |
                  Operation<'size', PathActions> |
                  Operation<'list', PathActions> |
                  Operation<'copy', CopyAction>;

// tslint:disable-next-line:cyclomatic-complexity
export async function backend(event: MessageEvent): Promise<void> {
    const data: Operations = event.data;

    let returnValue: null | string | Array<string> | void | number = null;

    try {
        switch (data.action) {
            case 'open':
                returnValue = await open(
                    data.arguments.path,
                    data.arguments.mode,
                    data.arguments.converter
                );
                break;
            case 'close':
                returnValue = close(
                    data.arguments.descriptor
                );
                break;
            case 'flush':
                returnValue = flush(
                    data.arguments.descriptor
                );
                break;
            case 'write':
                returnValue = await write(
                    data.arguments.descriptor,
                    data.arguments.data,
                );
                break;
            case 'read':
                returnValue = await read(
                    data.arguments.descriptor,
                    data.arguments.size
                );
                break;
            case 'remove':
                returnValue = await remove(
                    data.arguments.path
                );
                break;
            case 'size':
                returnValue = await size(
                    data.arguments.path
                );
                break;
            case 'list':
                returnValue = await list(
                    data.arguments.path
                );
                break;
            case 'copy':
                returnValue = await copy(
                    data.arguments.source,
                    data.arguments.destination,
                    data.arguments.override
                );
                break;
            default:
                throw new Error('Unsupported action');
        }
        _sendReply(data.transaction_id, false, returnValue);
    } catch (error) {
        _sendReply(data.transaction_id, true, error.stack);
    }
}

function close(descriptor: string): void {
    delete fdIndex[descriptor];
}

async function copy(src: string, dst: string, override: boolean): Promise<void> {
    src = unifyPath(src);
    dst = unifyPath(dst);

    if (src.endsWith('/') && dst.endsWith('/')) {
        const entries: Array<string> = await list(src);
        await Promise.all<void>(
            entries.map((value: string): Promise<void> => {
                return copy(src + value, dst + value, override);
            })
        );
        return;
    }

    if (src.endsWith('/') && !dst.endsWith('/')) {
        throw new Error('The `dst` has to end with `/` if the `src` ends with `/`');
    }

    if (dst.endsWith('/') && !src.endsWith('/')) {
        dst = dst + getFileName(src);
    }

    const content: string = await storageGet(src);
    if (content != null) {
        if (override || (await storageGet(dst)) == null) {
            return storageSet(
                dst,
                await storageGet(src)
            );
        }

        throw new Error('File already exists: ' + dst);
    }

    throw new Error('Source not found: ' + src);
}

function flush(descriptor: string): void {
    if (fdIndex[descriptor] == null) {
        throw new Error('Invalid file descriptor');
    }
}

async function list(path: string): Promise<Array<string>> {
    path = unifyPath(path, true);

    const l: number = path.length;
    const keys: Array<string> = await storageKeys();
    const entries: Array<string> = keys.filter((value: string): boolean => {
        return value.slice(0, l) === path;
    }).map((value: string): string => {
        const relPath: string = value.slice(l, value.length);
        const dirPos: number = relPath.indexOf('/');
        if (dirPos !== -1) {
            return relPath.slice(0, dirPos + 1);
        }
        return relPath;
    }).filter((value: string, pos: number, filteredArray: Array<string>): boolean => {
        return filteredArray.indexOf(value) === pos;
    });

    if (entries.length === 0 && path !== '/') {
        // FixMe: add support for empty directories
        throw new Error('No such directory');
    }

    return entries;
}

async function open(path: string, mode: 'r' | 'w' | 'a', converter: 'base64' | ''): Promise<string> {
    path = unifyPath(path);

    if (['a', 'w', 'r'].indexOf(mode) === -1) {
        throw new Error('Unsupported mode: ' + mode);
    }

    const content: string = await storageGet(path);
    if (content == null) {// (await storageGet(path)) == null) {
        if (mode !== 'r') {
            await storageSet(path, '');
        } else {
            throw new Error('No such file: ' + path);
        }
    } else if (mode === 'w') {
        await storageSet(path, '');
    }

    const id: string = (fdCount++).toString();
    let position: number = 0;

    fdIndex[id] = ((): FileWrapper => {
        return {
            read: async (length: number): Promise<string> => {
                const rawData: string = (await storageGet(path)).slice(position, position + length);
                position += length;

                if (converter === 'base64') {
                    return window.btoa(rawData);
                }

                if (converter === '') {
                    return rawData;
                }

                throw new Error('Unsupported converter: ' + converter);
            },
            write: async (data: string): Promise<void> => {
                if (converter === 'base64') {
                    await storageAdd(path, window.atob(data));
                } else if (converter === '') {
                    await storageAdd(path, data);
                } else {
                    throw new Error('Unsupported converter: ' + converter);
                }
            }
        };
    })();

    return id;
}

function read(descriptor: string, length: number): Promise<string> {
    return fdIndex[descriptor].read(length);
}

async function remove(path: string): Promise<void> {
    path = unifyPath(path);

    if (path === '/') {
        throw new Error('Forbidden');
    }

    if (path.endsWith('/')) {
        const entries: Array<string> = await list(path);
        await Promise.all<void>(
            entries.map((value: string): Promise<void> => {
                return remove(path + value);
            })
        );
    } else {
        return storageDel(path);
    }
}

async function size(path: string): Promise<number> {
    path = unifyPath(path);

    if (path.endsWith('/')) {
        const entries: Array<string> = await list(path);
        const sizes: Array<number> = await Promise.all<number>(
            entries.map((value: string): Promise<number> => {
                return size(path + value);
            })
        );
        return sizes.reduce((a: number, b: number): number => {
            return a + b;
        });
    }

    return (await storageGet(path)).length;
}

function write(descriptor: string, data: string): Promise<void> {
    return fdIndex[descriptor].write(data);
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/submodules/app-api/Filesystem/DebugBackend.ts